import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import BEM from 'common/bem';

const classes = BEM.with('ConversationThreadPanel');
const scrimClasses = BEM.with('ConversationThreadPanelScrim');

type ConversationMessage = {
  messageId?: string;
  sentAt?: string;
  senderDisplayName?: string;
  body?: string;
  hasAttachment?: boolean;
  conversationId?: string;
  channelId?: string | null;
};

type ConversationMetadata = {
  conversationId: string;
  channelId: string | null;
  messageCount: number;
};

type ConversationClient = {
  archiving: {
    getConversation: (
      organizationId: string,
      conversationId: string,
      options?: { limit?: number }
    ) => Promise<{ data: { messages: ConversationMessage[] } }>;
    getConversationMetadata: (
      organizationId: string,
      conversationId: string
    ) => Promise<{ data: ConversationMetadata }>;
  };
};

type ConversationThreadPanelProps = {
  client: ConversationClient | null;
  currentOrganizationId: string;
  conversationId: string;
  focusedMessageId?: string;
  onClose: () => void;
};

/**
 * "1:1 Direct" vs "Group" is a placeholder derived from channelId's presence — per xmpp's
 * conversation model, channelId is null for 1:1 messages and populated for group/distribution-list
 * ones. This isn't a real channel-type field (none is confirmed on the Snowflake schema yet); it's
 * the best signal already proven to exist until data eng confirms a dedicated column. Sourced from
 * the metadata endpoint rather than the loaded messages array, so it stays correct once the
 * messages endpoint paginates — see design doc 0002 §5.12.
 */
function deriveConversationTypeLabel(metadata: ConversationMetadata): string {
  return metadata.channelId ? 'Group' : '1:1 Direct';
}

export function ConversationThreadPanel({
  client,
  currentOrganizationId,
  conversationId,
  focusedMessageId,
  onClose,
}: ConversationThreadPanelProps) {
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<ConversationMetadata | null>(null);
  const focusedMessageRef = useRef<HTMLLIElement | null>(null);
  const hasScrolledToFocusedMessage = useRef(false);

  useEffect(() => {
    hasScrolledToFocusedMessage.current = false;

    if (!client) return;

    let isCancelled = false;
    setIsLoading(true);
    setError(null);

    client.archiving
      .getConversation(currentOrganizationId, conversationId)
      .then((response) => {
        if (isCancelled) return;
        setMessages(response?.data?.messages || []);
      })
      .catch(() => {
        if (isCancelled) return;
        setError('Something went wrong loading this conversation. Please try again.');
        setMessages([]);
      })
      .finally(() => {
        if (isCancelled) return;
        setIsLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [client, currentOrganizationId, conversationId]);

  useEffect(() => {
    if (!client) return;

    let isCancelled = false;
    setMetadata(null);

    client.archiving
      .getConversationMetadata(currentOrganizationId, conversationId)
      .then((response) => {
        if (isCancelled) return;
        setMetadata(response?.data || null);
      })
      .catch(() => {
        if (isCancelled) return;
        setMetadata(null);
      });

    return () => {
      isCancelled = true;
    };
  }, [client, currentOrganizationId, conversationId]);

  useEffect(() => {
    if (hasScrolledToFocusedMessage.current) return;
    if (!focusedMessageRef.current) return;

    focusedMessageRef.current.scrollIntoView({ block: 'center' });
    hasScrolledToFocusedMessage.current = true;
  }, [messages]);

  // Placeholder — this view only ever loads one page today. Wiring this up to fetch older/newer
  // messages as the user scrolls near either edge is deferred until real pagination exists on the
  // conversation endpoint.
  function onScroll() {}

  return (
    <>
      <div
        aria-hidden="true"
        className={scrimClasses()}
        data-test-id="conversation-thread-scrim"
        onClick={onClose}
      />
      <div className={classes()} data-test-id="conversation-thread-panel" role="dialog">
        <div className={classes('header')}>
          <button
            aria-label="Close conversation"
            className={classes('close-button')}
            data-test-id="conversation-thread-close"
            onClick={onClose}
            type="button"
          >
            ×
          </button>
          <div className={classes('header-text')}>
            <div className={classes('header-title')}>
              {metadata ? deriveConversationTypeLabel(metadata) : 'Conversation'}
            </div>
            <div className={classes('header-subtitle')}>
              {metadata &&
                `${metadata.messageCount} message${metadata.messageCount === 1 ? '' : 's'}`}
            </div>
          </div>
          <button
            aria-disabled="true"
            className={classes('export-button')}
            data-test-id="conversation-add-to-export"
            disabled
            title="Coming soon"
            type="button"
          >
            Add to Export
          </button>
        </div>

        <div className={classes('main')}>
          <div
            className={classes('body')}
            data-test-id="conversation-thread-body"
            onScroll={onScroll}
          >
            {isLoading && <div className={classes('loading-state')}>Loading conversation…</div>}
            {error && <div className={classes('error')}>{error}</div>}
            {!isLoading && !error && messages.length === 0 && (
              <div className={classes('empty-state')}>No messages found in this conversation.</div>
            )}
            {!isLoading && !error && messages.length > 0 && (
              <ul className={classes('message-list')}>
                {messages.map((message, index) => (
                  <li
                    className={classes('message', {
                      focused: message.messageId === focusedMessageId,
                    })}
                    key={message.messageId || index}
                    ref={message.messageId === focusedMessageId ? focusedMessageRef : undefined}
                  >
                    <div className={classes('message-meta')}>
                      <span className={classes('message-sender')}>{message.senderDisplayName}</span>
                      <span className={classes('message-date')}>
                        {message.sentAt && moment(message.sentAt).format('MMM D, YYYY h:mm A')}
                      </span>
                    </div>
                    {message.body && <div className={classes('message-body')}>{message.body}</div>}
                    {message.hasAttachment && (
                      <div className={classes('message-attachment')}>📎 Attachment</div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className={classes('details')}>
            <div className={classes('details-title')}>Details</div>
            <div className={classes('details-row')}>
              <span className={classes('details-label')}>Conversation ID</span>
              <span className={classes('details-value')}>{conversationId}</span>
            </div>
            <div className={classes('details-row')}>
              <span className={classes('details-label')}>Type</span>
              <span className={classes('details-value')}>
                {metadata ? deriveConversationTypeLabel(metadata) : '—'}
              </span>
            </div>
            <div className={classes('details-row')}>
              <span className={classes('details-label')}>Thread size</span>
              <span className={classes('details-value')}>
                {metadata ? `${metadata.messageCount} messages` : '—'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ConversationThreadPanel;
